<template>
  <div class="container v-content-container">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-cog"></i>
          <span v-if="selectedVenuePriceId">
            {{
              `${$label("venue.venue", "Venue")}${
                noDataExist ? "" : ": " + venueName
              }`
            }}
          </span>
          <span v-else>
            {{ $label("venue.new_price", "New venue price") }}
          </span>
        </h1>
      </div>
    </div>

    <navigation-tabs :venue-id="selectedVenueId"></navigation-tabs>

    <div class="row">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <div v-show="loading" class="row">
        <div class="col-xs-12">
          <view-loader
            margin-top-pixels="15"
            border-color="primary"
          ></view-loader>
        </div>
      </div>

      <div v-show="!loading" id="venue-price-form">
        <section>
          <div class="row">
            <div class="col-sm-6 col-xs-12 mt-6">
              <h2>
                <i class="fa fa-fw fa-lg fa-cog"></i>
                {{ $label("venue.venue_settings", "Settings") }}
              </h2>
              <p></p>
              <div class="v-form-partial">
                <div class="v-form">
                  <fieldset>
                    <div class="row">
                      <!-- BEGIN Price name -->
                      <section class="col col-12">
                        <text-input
                          v-model="selectedVenuePrice.name"
                          id="price-name"
                          :label="$label('venue.designation', 'designation')"
                          :is-disabled="updating"
                          :has-error="hasError('name')"
                          :error="getError('name')"
                          @keyup.native.enter="createOrUpdatePrice"
                        />
                      </section>
                      <!-- END Price name -->
                    </div>
                    <div class="row">
                      <!-- BEGIN Price form -->
                      <section class="col col-4">
                        <label class="label">
                          {{ $label("venue.price_input", "input") }}
                        </label>

                        <label class="select">
                          <select
                            id="price-form"
                            :disabled="updating"
                            v-model="selectedVenuePrice.form"
                          >
                            <option value="net">Net</option>
                            <option value="gross">Gross</option>
                          </select>
                          <i></i>
                        </label>

                        <p v-if="hasError('form')" class="text-danger">
                          {{ getError("form") }}
                        </p>
                      </section>
                      <!-- END Price form -->
                      <!-- BEGIN Price VAT-->
                      <section class="col col-4">
                        <label class="label">
                          {{ $label("venue.price_vat", "VAT") }}
                        </label>

                        <label class="select">
                          <select
                            id="price-vat"
                            :disabled="updating"
                            v-model="selectedVenuePrice.vat_id"
                          >
                            <option
                              v-for="vat in availableVats"
                              :value="vat.id"
                              :key="vat.id"
                              >{{ vat.name }}</option
                            >
                          </select>
                          <i></i>
                        </label>

                        <p v-if="hasError('vat_id')" class="text-danger">
                          {{ getError("vat_id") }}
                        </p>
                      </section>
                      <!-- END Price VAT -->
                      <!-- BEGIN Price amount-->
                      <section class="col col-4">
                        <text-input
                          v-model="selectedVenuePrice.amount"
                          id="price-amount"
                          :label="
                            `${$label('venue.venue_price', 'Price')} / ${$label(
                              'venue.venue_hour',
                              'hour'
                            )}`
                          "
                          :placeholder="$label('venue.venue_price', 'Price')"
                          :is-disabled="updating"
                          :has-error="hasError('amount')"
                          :error="getError('amount')"
                          @keyup.native.enter="createOrUpdatePrice"
                        />
                      </section>
                      <!-- END Price amount -->
                    </div>
                    <div class="row">
                      <!-- BEGIN Price description -->
                      <section class="col col-12">
                        <label class="label">
                          {{ $label("venue.price_description", "description") }}
                        </label>
                        <label class="textarea">
                          <textarea
                            v-model="selectedVenuePrice.description"
                            :class="{
                              'text-input-error': hasError('description')
                            }"
                            :placeholder="
                              $label('venue.price_description', 'description')
                            "
                            :disabled="updating"
                            id="price-description"
                            rows="5"
                          >
                          </textarea>
                        </label>
                        <p v-if="hasError('description')" class="text-danger">
                          {{ getError("description") }}
                        </p>
                      </section>
                      <!-- END Price description -->
                    </div>
                    <div class="row">
                      <!--BEGIN Price usable online-->
                      <section class="col col-8">
                        <label class="label">
                          Display this price online
                        </label>
                      </section>
                      <section class="col col-4">
                        <div
                          class="toggle-button pull-right bottom"
                          @click="
                            selectedVenuePrice.usable_online = !selectedVenuePrice.usable_online
                          "
                          id="price-usable-online"
                          :value="selectedVenuePrice.usable_online"
                        >
                          <template v-if="selectedVenuePrice.usable_online">
                            <i class="fa fa-circle text-green"></i>
                            <label class="input">
                              {{ $label("common.yes", "Yes") }}
                            </label>
                          </template>
                          <template v-else>
                            <label class="input">
                              {{ $label("common.no", "No") }}
                            </label>
                            <i class="fa fa-circle text-green"></i>
                          </template>
                        </div>
                      </section>
                      <!--END Price usable online-->
                    </div>
                    <div class="row">
                      <!-- BEGIN Price venues -->
                      <section class="col col-12">
                        <label class="label typo__label">{{
                          $label(
                            "venue.price_venues",
                            "Applies to the following venues"
                          )
                        }}</label>
                        <multiselect
                          v-model="attachedVenues"
                          tag-placeholder=""
                          placeholder="Search or add venue"
                          label="name"
                          track-by="id"
                          :options="venueOptions"
                          class="v-multiselect v-form"
                          :multiple="true"
                          :taggable="false"
                          :show-labels="false"
                          @tag="attachVenue"
                        >
                        </multiselect>

                        <p
                          v-if="hasError('venue_ids_to_attach')"
                          class="text-danger"
                        >
                          {{ getError("venue_ids_to_attach") }}
                        </p>
                      </section>
                      <!-- END Price venues -->
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="row mb-4">
        <div class="col-xs-12">&nbsp;</div>
      </div>
      <hr />

      <div class="row">
        <div class="col-xs-12 text-center">
          <back-button
            back-link="VenuePrices"
            :class="{ 'is-disabled': updating }"
            id="back-venue-date-settings-button"
            icon="fa fa-lg fa-fw fa-arrow-circle-o-left"
            :text="$label('common.back', 'Back')"
          >
          </back-button>
          &nbsp;
          <info-button
            :class="{ 'is-disabled': updating }"
            id="save-price-button"
            icon="fa fa-lg fa-fw fa-save"
            :text="$label('common.save', 'Save')"
            @click.native="createOrUpdatePrice"
          >
          </info-button>
          &nbsp;
          <danger-button
            :class="{
              'is-disabled': updating || selectedVenuePriceId == null
            }"
            id="delete-price-button"
            icon="fa fa-fw fa-lg fa-trash-o"
            :text="$label('common.delete', 'Delete')"
            @click.native="deleteVenuePrice"
          >
          </danger-button>
        </div>
        <div class="row mb-4">
          <div class="col-xs-12">&nbsp;</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api.js";
import FormErrorsMixin from "@/mixins/FormErrors";

import NavigationTabs from "@/components/Settings/Venue/NavigationTabs.vue";
import ViewLoader from "@/components/Common/ViewLoader";
import TextInput from "@/components/Common/Inputs/TextInput.vue";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import BackButton from "@/components/Common/Buttons/BackButton";
import DangerButton from "@/components/Common/Buttons/DangerButton";
import Error404 from "@/components/Common/Error404";
import Multiselect from "vue-multiselect";
import swal from "sweetalert";

export default {
  name: "PriceDetails.vue",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    NavigationTabs,
    TextInput,
    InfoButton,
    BackButton,
    DangerButton,
    Multiselect,
    Error404
  },

  data() {
    return {
      loading: false,
      updating: false,
      noDataExist: false,
      availableVats: null,
      facilities: [],
      venueOptions: [],
      selectedVenue: {
        name: null
      },
      selectedVenueId: null,
      selectedVenuePriceId: null,
      selectedVenuePrice: {
        type: "default",
        name: null,
        description: null,
        form: "net",
        amount: 0,
        vat_id: 1,
        usable_online: false,
        venue_ids_to_attach: [],
        public_holiday: false
      },
      attachedVenues: [],
      weekdays: [
        {
          id: 0,
          name: "Monday",
          checked: false
        },
        {
          id: 1,
          name: "Tuesday",
          checked: false
        },
        {
          id: 2,
          name: "Wednesday",
          checked: false
        },
        {
          id: 3,
          name: "Thursday",
          checked: false
        },
        {
          id: 4,
          name: "Friday",
          checked: false
        },
        {
          id: 5,
          name: "Saturday",
          checked: false
        },
        {
          id: 6,
          name: "Sunday",
          checked: false
        }
      ],
      validatedInputs: [
        "name",
        "description",
        "form",
        "vat_id",
        "amount",
        "venue_ids_to_attach"
      ]
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedVenueId = vm.$route.params.id;
      vm.selectedVenuePriceId = vm.$route.params.price_id;
      if (vm.$route.params.price_type) {
        vm.selectedVenuePrice.type = vm.$route.params.price_type;
      }
      // Get Facilities
      vm.getFacilities(() => {
        //
      });

      // Get Venue
      if (
        typeof vm.selectedVenueId !== "undefined" &&
        vm.selectedVenueId !== null
      ) {
        vm.getVenue(() => {
          //
        });
      }

      // Get Secondary Price
      if (
        typeof vm.selectedVenuePriceId !== "undefined" &&
        vm.selectedVenuePriceId !== null
      ) {
        if (vm.$route.query.venue_price_created) {
          swal("Success!", "The price was created.", "success").then(() => {
            window.history.pushState(
              {},
              document.title,
              window.location.pathname
            );
          });
        }
        vm.getVenuePrice(() => {
          //
        });
      }
      // Get Available vats
      vm.getVats(() => {
        //
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    // Get Facilities
    this.getFacilities(() => {
      //
    });

    // Get Venue
    if (
      typeof this.selectedVenueId !== "undefined" &&
      this.selectedVenueId !== null
    ) {
      this.getVenue(() => {
        //
      });
    }

    // Get Secondary Price
    if (
      typeof this.selectedVenuePriceId !== "undefined" &&
      this.selectedVenuePriceId !== null
    ) {
      this.getVenuePrice(() => {
        //
      });
    }
    // Get Available vats
    this.getVats(() => {
      //
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    attachVenue(venue) {
      this.attachedVenues.push(venue);
    },

    toggleCheckbox(id) {
      this.weekdays[id].checked = !this.weekdays[id].checked;
    },

    getFacilities(successCallback) {
      const endpoint = this.$partnerEndpoint("facilities/simplified");

      Api()
        .get(endpoint)
        .then(response => {
          this.facilities = response.data.data;
          if (!this.selectedVenueId || this.selectedVenueId === "default") {
            const selectedFacility = this.facilities.find(
              facility => facility.venues.length > 0
            );
            if (selectedFacility) {
              this.selectedVenueId = selectedFacility.venues[0].id;
            }
          }
          this.facilities.map(facility => {
            facility.venues.map(venue => {
              this.venueOptions.push(venue);
            });
          });
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    createOrUpdatePrice() {
      if (this.selectedVenuePriceId == null) {
        this.createVenuePrice();
      } else {
        this.updateVenuePrice();
      }
    },

    getVenue() {
      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint("venues/" + this.selectedVenueId);

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedVenue = response.data.data;
          if (!this.selectedVenuePriceId) {
            this.attachedVenues.push(this.selectedVenue);
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getVenuePrice() {
      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        "venue-prices/" + this.selectedVenuePriceId
      );

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedVenuePrice.amount =
            response.data.data.form === "net"
              ? response.data.data.amount_net
              : response.data.data.amount_gross;
          this.selectedVenuePrice.vat_id = response.data.data.vat.id;
          this.selectedVenuePrice.type = response.data.data.type;
          this.selectedVenuePrice.name = response.data.data.name;
          this.selectedVenuePrice.form = response.data.data.form;
          this.selectedVenuePrice.usable_online =
            response.data.data.usable_online;
          this.selectedVenuePrice.description = response.data.data.description;
          this.attachedVenues = response.data.data.venues;
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateVenuePrice() {
      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "venue-prices/" + this.selectedVenuePriceId
      );
      this.selectedVenuePrice.venue_ids_to_attach = this.attachedVenues.map(
        venue => venue.id
      );

      Api()
        .put(endpoint, this.selectedVenuePrice)
        .then(response => {
          this.selectedVenuePriceId = response.data.data.id;
          this.getVenuePrice();
        })
        .catch(error => {
          console.log(error);
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    createVenuePrice() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("venue-prices");
      this.selectedVenuePrice.venue_ids_to_attach = this.attachedVenues.map(
        venue => venue.id
      );

      Api()
        .post(endpoint, this.selectedVenuePrice)
        .then(response => {
          this.selectedVenuePriceId = response.data.data.id;
          this.$router.push({
            name: "VenuePriceDetails",
            params: {
              id: this.selectedVenueId,
              price_id: this.selectedVenuePriceId
            },
            query: { venue_price_created: true }
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    deleteVenuePrice() {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The price will be deleted and will not be able to use it",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "venue-prices/" + this.selectedVenuePriceId
          );

          Api()
            .delete(endpoint)
            .then(response => {
              console.log(response.status);
              this.$router.push({
                name: "VenuePrices",
                query: { venue_price_deleted: true }
              });
            })
            .catch(error => {
              console.log(error.response);
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete venue prices.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    getVats() {
      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint("vats");

      Api()
        .get(endpoint)
        .then(response => {
          this.availableVats = response.data.data;
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },

  computed: {
    venueName() {
      return this.selectedVenue ? this.selectedVenue.name : "";
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.mb-4 {
  margin-bottom: 1.2rem;
}

.mt-2 {
  margin-top: 0.6rem;
}

.mt-6 {
  margin-top: 1.8rem;
}

.toggle-button {
  width: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 3px solid #3aa94c;
  padding: 2px 5px;
  cursor: pointer;
}

.toggle-button * {
  cursor: pointer;
}

.checked-weekday {
  color: #333;
  background-color: #e6e6e6;
  border-color: #adadad;
}

#venue-public-holiday {
  font-size: 1.5rem;
  padding: 2px 4px !important;
}

@media screen and (max-width: 600px) {
  .toggle-button {
    margin-top: 0;
    margin-bottom: 1.3rem;
  }
}
</style>
